import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ServiceAndLocation } from '@voice/utils';
import { ClientOnboardingService } from '../../../services/client-onboarding.service';
import { NotificationsService } from '../../../services/notifications.service';
import { SitesService } from '../../../services/sites.service';
import { Button } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { ConfirmationService, PrimeTemplate } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { LocationFormComponent } from './location-form.component';
import { WizardButtonsComponent } from './wizard-buttons.component';
import { downloadCSV } from '../../../utils/file-manager';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CardModule } from 'primeng/card';

@Component({
  selector: 'app-client-onboarding-locations',
  standalone: true,
  imports: [
    Button,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    PrimeTemplate,
    TableModule,
    DialogModule,
    WizardButtonsComponent,
    ConfirmDialogModule,
    CardModule,
  ],
  providers: [DialogService, ConfirmationService],
  templateUrl: './locations-list.component.html',
})
export class LocationsListComponent implements OnInit {
  constructor(
    private readonly dialogService: DialogService,
    private readonly sitesService: SitesService,
    private readonly clientOnboardingService: ClientOnboardingService,
    private readonly notificationService: NotificationsService,
    private readonly confirmationService: ConfirmationService
  ) {}

  @Input() locations!: ServiceAndLocation[];
  @Input() onBoardingId?: number;
  @Input() domain?: string;

  @Output() locationsReady = new EventEmitter<string>();

  loading = false;

  ngOnInit(): void {
    if (!this.locations) {
      this.locations = [];
    }

    if (!this.domain) {
      return;
    }

    this.loading = true;
    this.sitesService.getSitesInDomain(this.domain, true).subscribe({
      next: (sites) => {
        if (this.locations.length === 0) {
          this.locations = sites.map((site) => this.buildLocationFromSite(site));
        }
        this.loading = false;
      },
      error: (err) => {
        this.notificationService.error(err);
        this.loading = false;
      },
    });
  }

  add(): void {
    const config: DynamicDialogConfig = {
      data: { record: undefined },
      header: 'Add location',
      modal: true,
      closable: true,
      width: `${window.innerWidth * 0.75}px`,
      contentStyle: { 'overflow-y': 'auto' },
    };

    this.dialogService.open(LocationFormComponent, config).onClose.subscribe({
      next: (added: ServiceAndLocation) => {
        if (added) {
          this.locations = [...this.locations, added];
        }
      },
    });
  }

  edit(location: ServiceAndLocation): void {
    const config: DynamicDialogConfig = {
      data: { record: location },
      header: 'Edit location',
      modal: true,
      closable: true,
      width: `${window.innerWidth * 0.75}px`,
      contentStyle: { 'overflow-y': 'auto' },
    };

    this.dialogService.open(LocationFormComponent, config).onClose.subscribe({
      next: (updated: ServiceAndLocation) => {
        if (updated) {
          this.locations = this.locations.map((l) =>
            l === location ? updated : l
          );
        }
      },
    });
  }

  delete(location: ServiceAndLocation): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to remove location "${location.companyName}"?`,
      header: 'Delete location',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.locations = this.locations.filter((l) => l !== location);
      },
    });
  }

  onWizardButtonClick(direction: string): void {
    if (!this.onBoardingId) {
      this.notificationService.error('No onboarding record selected').then();
      return;
    }

    if (this.locations.length === 0) {
      this.locationsReady.emit(direction);
      return;
    }

    this.clientOnboardingService
      .saveLocations(this.onBoardingId, this.locations)
      .subscribe({
        next: () => {
          this.notificationService
            .success('Locations saved successfully')
            .then(() => this.locationsReady.emit(direction));
        },
      });
  }

  exportLocations(): void {
    if (!this.locations?.length) {
      this.notificationService.error('No locations to export');
      return;
    }

    const columns = [
      'companyName',
      'firstName',
      'lastName',
      'addressLine1',
      'addressLine2',
      'city',
      'state',
      'zipCode',
    ];

    downloadCSV(`locations_${this.domain ?? 'export'}`, columns, this.locations);
  }

  private buildLocationFromSite(site: any): ServiceAndLocation {
    if (!site.address) {
      return { companyName: site.site };
    }

    const nameParts: string[] = site.address.callerName?.split(' ') ?? [];
    const [zipCode] = site.address.addressPostalCode?.split('-') ?? [];

    return {
      companyName: site.site,
      firstName: nameParts[0],
      lastName: nameParts.length > 1 ? nameParts.slice(1).join(' ') : undefined,
      addressLine1: site.address.addressLineOne,
      addressLine2: site.address.addressLineTwo,
      state: site.address.addressState,
      city: site.address.addressCity,
      zipCode,
    };
  }
}
